import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Skeleton from '@mui/material/Skeleton';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import MouseIcon from '@mui/icons-material/Mouse';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PercentIcon from '@mui/icons-material/Percent';
import { BarChart } from '@mui/x-charts/BarChart';
import { DataGridPremium, type GridColDef } from '@mui/x-data-grid-premium';
import { format, subDays } from 'date-fns';

import StatCard from '../components/StatCard';
import DateRangePicker from '../components/DateRangePicker';
import { getSummary, getGraph, getBreakdown } from '../services/stats.service';
import type { SnapshotData, GraphPoint, BreakdownRow } from '../types';

type GroupBy = 'country' | 'channel';

function safeChange(val: number | null | undefined): number {
  return val ?? 0;
}

function fmtNumber(n: number): string {
  return (n ?? 0).toLocaleString();
}

function fmtCurrency(n: number): string {
  return '$' + (n ?? 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function fmtPercent(n: number): string {
  return (n ?? 0).toFixed(2) + '%';
}

const groupByLabel: Record<GroupBy, string> = {
  country: 'Country',
  channel: 'Channel',
};

const DashboardPage: React.FC = () => {
  const [from, setFrom] = useState(() => format(subDays(new Date(), 6), 'yyyy-MM-dd'));
  const [to, setTo] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [groupBy, setGroupBy] = useState<GroupBy>('country');

  const [snapshot, setSnapshot] = useState<SnapshotData | null>(null);
  const [graph, setGraph] = useState<GraphPoint[]>([]);
  const [breakdown, setBreakdown] = useState<BreakdownRow[]>([]);

  const [loadingSnapshot, setLoadingSnapshot] = useState(true);
  const [loadingGraph, setLoadingGraph] = useState(true);
  const [loadingBreakdown, setLoadingBreakdown] = useState(true);

  const handleDateChange = useCallback((newFrom: string, newTo: string) => {
    setFrom(newFrom);
    setTo(newTo);
  }, []);

  // Load snapshot and graph when date range changes
  useEffect(() => {
    let cancelled = false;

    setLoadingSnapshot(true);
    setLoadingGraph(true);

    Promise.all([
      getSummary(from, to),
      getGraph(from, to),
    ])
      .then(([snapData, graphData]) => {
        if (cancelled) return;
        setSnapshot(snapData);
        setGraph(graphData);
      })
      .catch((err) => {
        console.error('Failed to load dashboard data:', err);
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingSnapshot(false);
          setLoadingGraph(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [from, to]);

  // Load breakdown when date range or groupBy changes
  useEffect(() => {
    let cancelled = false;

    setLoadingBreakdown(true);

    getBreakdown(from, to, groupBy)
      .then((data) => {
        if (cancelled) return;
        setBreakdown(data);
      })
      .catch((err) => {
        console.error('Failed to load breakdown:', err);
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingBreakdown(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [from, to, groupBy]);

  const crValue = snapshot
    ? snapshot.clicks > 0
      ? (snapshot.conversions / snapshot.clicks) * 100
      : 0
    : 0;

  // Breakdown columns
  const breakdownColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: groupByLabel[groupBy],
      flex: 1,
      minWidth: 160,
      valueGetter: (_value: unknown, row: BreakdownRow) => row.name || row.group_key,
    },
    {
      field: 'clicks',
      headerName: 'Clicks',
      width: 110,
      type: 'number',
      valueFormatter: (value: number) => fmtNumber(value),
    },
    {
      field: 'conversions',
      headerName: 'Conversions',
      width: 130,
      type: 'number',
      valueFormatter: (value: number) => fmtNumber(value),
    },
    {
      field: 'payout',
      headerName: 'Payout',
      width: 120,
      type: 'number',
      valueFormatter: (value: number) => fmtCurrency(value),
    },
    {
      field: 'cr',
      headerName: 'CR%',
      width: 90,
      type: 'number',
      valueFormatter: (value: number) => fmtPercent(value),
    },
    {
      field: 'epc',
      headerName: 'EPC',
      width: 90,
      type: 'number',
      valueFormatter: (value: number) => fmtCurrency(value),
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Typography variant="h4">Dashboard</Typography>
        <DateRangePicker from={from} to={to} onChange={handleDateChange} />
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {loadingSnapshot ? (
            <Skeleton variant="rounded" height={140} sx={{ borderRadius: 3 }} />
          ) : (
            <StatCard
              label="Clicks"
              value={snapshot?.clicks ?? 0}
              change={safeChange(snapshot?.changes?.clicks)}
              color="#6C8EFF"
              icon={<MouseIcon />}
              format="number"
            />
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {loadingSnapshot ? (
            <Skeleton variant="rounded" height={140} sx={{ borderRadius: 3 }} />
          ) : (
            <StatCard
              label="Conversions"
              value={snapshot?.conversions ?? 0}
              change={safeChange(snapshot?.changes?.conversions)}
              color="#FFB74D"
              icon={<ShoppingCartCheckoutIcon />}
              format="number"
            />
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {loadingSnapshot ? (
            <Skeleton variant="rounded" height={140} sx={{ borderRadius: 3 }} />
          ) : (
            <StatCard
              label="Payout"
              value={snapshot?.payout ?? 0}
              change={safeChange(snapshot?.changes?.payout)}
              color="#00E5A0"
              icon={<AccountBalanceWalletIcon />}
              format="currency"
            />
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {loadingSnapshot ? (
            <Skeleton variant="rounded" height={140} sx={{ borderRadius: 3 }} />
          ) : (
            <StatCard
              label="CR%"
              value={crValue}
              change={0}
              color="#00E5A0"
              icon={<PercentIcon />}
              format="percent"
            />
          )}
        </Grid>
      </Grid>

      {/* Chart */}
      <Card sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" sx={{ mb: 2.5 }}>
          Performance Overview
        </Typography>
        {loadingGraph ? (
          <Skeleton variant="rounded" height={350} sx={{ borderRadius: 2 }} />
        ) : (
          <BarChart
            height={350}
            xAxis={[
              {
                data: graph.map((p) => p.date),
                scaleType: 'band',
                tickLabelStyle: { fill: '#9CA3AF', fontSize: 12 },
              },
            ]}
            yAxis={[
              {
                tickLabelStyle: { fill: '#9CA3AF', fontSize: 12 },
              },
            ]}
            series={[
              {
                data: graph.map((p) => p.clicks),
                label: 'Clicks',
                color: '#6C8EFF',
              },
              {
                data: graph.map((p) => p.payout),
                label: 'Payout',
                color: '#00E5A0',
              },
            ]}
            sx={{
              width: '100%',
              '& .MuiChartsGrid-line': {
                stroke: 'rgba(255,255,255,0.06)',
              },
              '& .MuiChartsAxis-line': {
                stroke: 'rgba(255,255,255,0.12)',
              },
              '& .MuiChartsAxis-tick': {
                stroke: 'rgba(255,255,255,0.12)',
              },
            }}
            grid={{ horizontal: true }}
            slotProps={{
              legend: {
                sx: { '& text': { fill: '#9CA3AF !important', fontSize: 13 } },
              },
            }}
          />
        )}
      </Card>

      {/* Breakdown */}
      <Card sx={{ p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Typography variant="h5">Breakdown</Typography>
          <ToggleButtonGroup
            value={groupBy}
            exclusive
            onChange={(_e, val) => {
              if (val) setGroupBy(val as GroupBy);
            }}
            size="small"
          >
            <ToggleButton value="country">Country</ToggleButton>
            <ToggleButton value="channel">Channel</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {loadingBreakdown ? (
          <Skeleton variant="rounded" height={300} sx={{ borderRadius: 2 }} />
        ) : (
          <DataGridPremium
            rows={breakdown}
            columns={breakdownColumns}
            getRowId={(row: BreakdownRow) => row.group_key}
            density="compact"
            disableRowSelectionOnClick
            hideFooter={breakdown.length <= 100}
            initialState={{
              sorting: {
                sortModel: [{ field: 'clicks', sort: 'desc' }],
              },
            }}
            sx={{
              border: 'none',
              height: 400,
              '& .MuiDataGrid-columnHeaders': {
                borderBottom: '1px solid rgba(255,255,255,0.08)',
              },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid rgba(255,255,255,0.04)',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'rgba(108, 142, 255, 0.04)',
              },
            }}
          />
        )}
      </Card>
    </Box>
  );
};

export default DashboardPage;
